import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import { getReservationList } from "redux/actions/reservationAction";
import { getDateFormat, handlePermissions, isValidTripTime } from "utils/utils";
import useModal from "hooks/useModal";
import CancelRegistrationModal from "pages/operation/cancelRegistrationModal/CancelRegistrationModal";
import CommonSubHeader from "components/commonSubHeader/CommonSubHeader";
import CommonTable from "components/commonTable/CommonTable";
import Loader from "components/loader/Loader";
import { filterReservationdata, tripStatusUpdateForm } from "./ReservationData";
import { roleAndPermissionEnum } from "utils/rolesPermissionsEnum";
import CommonDialog from "components/commonDialog/CommonDialog";
import { updateTripStatusApi } from "services/TripService";

const Reservation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams?.get("startDate");
  const endDate = searchParams?.get("endDate");
  const filterType = searchParams?.get("type");
  const filterPage = searchParams?.get("page");
  const filterRowPerPage = searchParams?.get("rowPerPage");
  const filterSearch = searchParams?.get("search");
  const { state } = useLocation();
  const [page, setPage] = useState(filterPage ? parseInt(filterPage) : 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    filterRowPerPage ? filterRowPerPage : 10,
  );
  const [status, setStatus] = useState(
    filterType
      ? filterType?.split(",")
      : state?.value
        ? state?.value?.split(",")
        : "",
  );
  const [search, setSearch] = useState(filterSearch ? filterSearch : "");
  const [dateFrom, setDateFrom] = useState(
    startDate && startDate !== "null"
      ? startDate
      : startDate === "null"
        ? ""
        : state?.fromTab
          ? state?.dateFrom
          : getDateFormat(new Date(), "reservationDateFormat"),
  );
  let paramsList = {};

  searchParams.forEach((value, key) => {
    paramsList[key] = value;
  });

  const [dateTo, setDateTo] = useState(
    endDate && endDate !== "null"
      ? endDate
      : endDate === "null"
        ? ""
        : state?.fromTab
          ? state?.dateTo
          : getDateFormat(new Date(), "reservationDateFormat"),
  );
  const [tripDetails, setTripDetails] = useState();
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const { open: cancelModal, toggle: setCancelModal } = useModal();
  const reservationList = useSelector(
    (state) => state.getReservationList?.reservationList?.data,
  );

  const loader = useSelector((state) => state.getReservationList?.loading);
  let filterData = {
    page: page,
    dateFrom: dateFrom,
    dateTo: dateTo,
    status: status ? status : "",
    pageSize: rowsPerPage ? rowsPerPage : "",
    bookingType: "",
    search: search ? search : "",
  };

  useEffect(() => {
    dispatch(getReservationList(filterData));
  }, []);

  const pageNavigation = () => {
    navigate("/reservation/new-reservation");
  };

  const tableOnClickRow = (navigate, val) => {
    navigate(
      `/reservation/bookingdetails?id=${val.id}&startDate=${dateFrom}&endDate=${dateTo}&type=reservation`,
    );
  };

  const handleChangePage = (event, newPage) => {
    setSearchParams({ ...paramsList, page: newPage });
    setPage(newPage);
    dispatch(getReservationList({ ...filterData, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setSearchParams({
      ...paramsList,
      rowPerPage: parseInt(event.target.value),
      page: 0,
    });
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    dispatch(
      getReservationList({
        ...filterData,
        page: 0,
        pageSize: parseInt(event.target.value),
      }),
    );
  };

  const handleStatusUpdate = async (data) => {
    try {
      setStatusUpdateLoading(true);

      const payload = {
        tripId: selectedTrip.tripId,
        status: data.status,
        reason: data.reason || undefined,
        endOtp: 1234,
      };

      const response = await updateTripStatusApi(payload);

      if (response?.success) {
        toast.success("Trip status updated successfully");
        setOpenStatusDialog(false);
        // Refresh trip list
        dispatch(getReservationList(filterData));
      } else {
        toast.error(
          response?.data?.message ||
            response?.message ||
            "Failed to update trip status",
        );
      }
    } catch (error) {
      toast.error("Something went wrong while updating trip status");
      console.error("Trip status update error:", error);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const onClickMoreIcon = (data, type) => {
    if (type === "Edit") {
      const dateStatus = isValidTripTime(data?.scheduledTime);

      if (dateStatus) {
        navigate("/reservation/new-reservation", {
          state: { edit: true, reservationProfile: data },
        });
      } else {
        toast.error("You are not allowed to edit the trip at this time");
      }
    } else if (type === "Cancel Duty") {
      setTripDetails(data);
      setCancelModal();
    } else if (type === "Update Status") {
      setSelectedTrip(data);
      setOpenStatusDialog(true);
    }
  };

  const onSearch = debounce((e) => {
    setSearchParams({ ...paramsList, page: 0, search: e });
    setPage(0);
    setSearch(e);
    const searchFilterData = {
      ...filterData,
      search: e,
      page: 0,
      pageSize: "",
    };
    dispatch(getReservationList(searchFilterData));
  }, 500);

  const onFilterChange = (val) => {
    setPage(0);
    const defaultStatus = [
      "alloted",
      "dispatched",
      "booked",
      "cancelled",
      "billed",
      "onGoing",
      "completed",
    ];

    const filterStatus = val.filter((status) => {
      return defaultStatus.includes(status);
    });
    setSearchParams({ ...paramsList, type: filterStatus?.toString(), page: 0 });

    setStatus(val);

    const reservationFilterData = {
      ...filterData,
      status: val?.toString(),
      page: 0,
    };
    dispatch(getReservationList(reservationFilterData));
  };

  const onRestFilter = () => {
    setSearchParams({ ...paramsList, type: "", page: 0 });
    setStatus("");

    const reservationFilterData = {
      ...filterData,
      status: "",
      page: 0,
    };
    dispatch(getReservationList(reservationFilterData));
  };

  const onSelectDateRange = (newValue) => {
    if (newValue === null || newValue === 0) {
      setSearchParams({
        ...paramsList,
        startDate: "null",
        endDate: "null",
        page: 0,
      });
      setDateFrom("");
      setDateTo("");
      filterData = { ...filterData, dateFrom: "", dateTo: "" };
    } else {
      setSearchParams({
        ...paramsList,
        startDate: getDateFormat(newValue[0], "reservationDateFormat"),
        endDate: getDateFormat(newValue[1], "reservationDateFormat"),
        page: 0,
      });
      setDateFrom(getDateFormat(newValue[0], "reservationDateFormat"));
      setDateTo(getDateFormat(newValue[1], "reservationDateFormat"));
      filterData = {
        ...filterData,
        dateFrom: getDateFormat(newValue[0], "reservationDateFormat"),
        dateTo: getDateFormat(newValue[1], "reservationDateFormat"),
      };
    }
    dispatch(getReservationList(filterData));
  };

  //roles and permissions
  const roleAndPermissions = useSelector(
    (state) => state?.getRolesAndPermissions?.rolesAndPermissions,
  );
  const reservationRoleEnum = roleAndPermissionEnum.reservation;

  const accessForall = handlePermissions(
    roleAndPermissions,
    reservationRoleEnum.moduleKey,
    {
      defaultAllAccess: reservationRoleEnum.all,
      customAccess: reservationRoleEnum.add,
    },
  );

  const accessForOnlyView = handlePermissions(
    roleAndPermissions,
    reservationRoleEnum.moduleKey,
    { customAccess: reservationRoleEnum.view },
  );

  const accessForEdit = "true";
  //  handlePermissions(roleAndPermissions, reservationRoleEnum.moduleKey, {customAccess: reservationRoleEnum.edit});

  const accessForAdd = handlePermissions(
    roleAndPermissions,
    reservationRoleEnum.moduleKey,
    { customAccess: reservationRoleEnum.add },
  );

  const accessForCancel = handlePermissions(
    roleAndPermissions,
    reservationRoleEnum.moduleKey,
    { customAccess: reservationRoleEnum.cancel },
  );

  const hasViewMoreIcon = accessForall || accessForEdit || accessForCancel;
  const addAccess = accessForall || accessForAdd;

  return (
    <Box>
      <Box className="mt10 mb30">
        <CommonSubHeader
          filter={true}
          filterData={filterReservationdata}
          buttonName={addAccess && "New Reservation"}
          navigation={pageNavigation}
          defaultStatus={status}
          onFilterChange={onFilterChange}
          onRestFilter={onRestFilter}
          onSelectDateRange={onSelectDateRange}
          onSearchQuery={onSearch}
          dateFrom={dateFrom}
          dateTo={dateTo}
          search={search}
        />
      </Box>

      {loader ? (
        <Loader />
      ) : (
        <CommonTable
          data={reservationList}
          isClickable={true}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          tableOnClickRow={tableOnClickRow}
          tabName={"reservation"}
          onClickMoreIcon={onClickMoreIcon}
          viewMoreIcon={!hasViewMoreIcon}
          accessForEdit={accessForEdit}
          accessForCancel={accessForCancel}
          accessForall={accessForall}
          enableTripHighlight={true}
          showDispatchLegend={true}
        />
      )}
      <CancelRegistrationModal
        page={"listPage"}
        open={cancelModal}
        toggle={setCancelModal}
        reservationData={tripDetails}
        payload={filterData}
      />

      {openStatusDialog && (
        <CommonDialog
          open={openStatusDialog}
          handleClose={() => setOpenStatusDialog(false)}
          formData={tripStatusUpdateForm(selectedTrip?.status)} // Pass current status
          button={"dialogButton"}
          onSubmittBtn={handleStatusUpdate}
          type={"Update Trip Status"}
          selectType={"edit"}
          loading={statusUpdateLoading}
          text={`Trip ID: ${selectedTrip?.tripId} currently has the status '${selectedTrip?.status}'. You’re about to update it. Please enter a reason for this update.`}
        />
      )}
    </Box>
  );
};

export default Reservation;
